/**
 * FleetStatus Component
 *
 * Displays each ship's name, size, and current status (afloat or sunk)
 * for both the player's fleet and the opponent's fleet.
 *
 * Requirements: 8.4, 8.5
 */

import React from 'react';
import {
  FleetStatusProps,
  ShipType,
  FleetStatus as FleetStatusType,
} from '../types';
import './FleetStatus.css';

/** Display names for ship types */
const SHIP_DISPLAY_NAMES: Record<ShipType, string> = {
  carrier: 'Carrier',
  battleship: 'Battleship',
  cruiser: 'Cruiser',
  submarine: 'Submarine',
  destroyer: 'Destroyer',
};

interface FleetSectionProps {
  title: string;
  fleet: FleetStatusType;
  showShipNames: boolean;
}

const FleetSection: React.FC<FleetSectionProps> = ({
  title,
  fleet,
  showShipNames,
}) => (
  <div className="bs-fleet-status__section">
    <div className="bs-fleet-status__title">{title}</div>
    <div className="bs-fleet-status__summary">
      {fleet.shipsAfloat} afloat · {fleet.shipsSunk} sunk
    </div>
    <div className="bs-fleet-status__list">
      {fleet.ships.map((ship) => (
        <div key={ship.shipType} className="bs-fleet-status__ship">
          <div
            className={`bs-fleet-status__ship-indicator ${
              ship.isSunk
                ? 'bs-fleet-status__ship-indicator--sunk'
                : 'bs-fleet-status__ship-indicator--afloat'
            }`}
          />
          <span
            className={`bs-fleet-status__ship-name ${
              ship.isSunk ? 'bs-fleet-status__ship-name--sunk' : ''
            }`}
          >
            {showShipNames || ship.isSunk
              ? SHIP_DISPLAY_NAMES[ship.shipType]
              : '???'}
          </span>
          <span className="bs-fleet-status__ship-size">({ship.size})</span>
          <span
            className={`bs-fleet-status__ship-status ${
              ship.isSunk
                ? 'bs-fleet-status__ship-status--sunk'
                : 'bs-fleet-status__ship-status--afloat'
            }`}
          >
            {ship.isSunk ? 'Sunk' : 'Afloat'}
          </span>
        </div>
      ))}
    </div>
  </div>
);

export const FleetStatusComponent: React.FC<FleetStatusProps> = ({
  playerFleet,
  opponentFleet,
  showOpponentShipNames,
}) => {
  return (
    <div className="bs-fleet-status" role="region" aria-label="Fleet Status">
      <FleetSection
        title="Your Fleet"
        fleet={playerFleet}
        showShipNames={true}
      />

      <div className="bs-fleet-status__divider" />

      <FleetSection
        title="Enemy Fleet"
        fleet={opponentFleet}
        showShipNames={showOpponentShipNames}
      />
    </div>
  );
};

export { FleetStatusComponent as FleetStatus };
export default FleetStatusComponent;
